import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageShell } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useHospitalDirectory, formatDoctorOption } from '../../hooks/useHospitalDirectory';
import { fetchPatientProfile, genderFromCode } from '../../utils/opIpLink';

const INSURANCE_OPTIONS = [
  'Cash', 'Star Health', 'HDFC ERGO', 'ICICI Lombard', 'New India Assurance',
  'United India', 'National Insurance', 'Bajaj Allianz', 'Reliance General',
  'Care Health', 'Niva Bupa', 'CGHS', 'ESIC', 'Ayushman Bharat (PMJAY)',
  'Mediclaim', 'Corporate (Self Pay)', 'Other Insurance',
];

const ADMIT_TYPES = ['Emergency', 'Planned Surgery', 'Medical Management', 'Maternity', 'ICU', 'Daycare', 'Observation', 'Referral Transfer', 'Accident / Trauma'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-', 'Unknown'];
const PAST_HISTORY = ['Diabetes Mellitus', 'Hypertension', 'CAD / IHD', 'CKD', 'Asthma / COPD', 'Hypothyroidism', 'Epilepsy', 'Liver Disease', 'HIV', 'Tuberculosis', 'Cancer', 'Stroke', 'Surgery (previous)', 'Psychiatric illness'];

export default function IpdAdmit() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const { data, loading, error } = useLiveData(() => ipApi.getBeds(), []);
  const allBeds = data?.beds || [];
  const vacantBeds = allBeds.filter((b) => b.status === 'vacant');
  const { data: dir } = useHospitalDirectory();
  const doctors = useMemo(() => dir?.doctors || [], [dir]);

  const [form, setForm] = useState({
    // Patient info
    patientName: params.get('patient') || '',
    patientId: params.get('patientId') || '',
    age: '',
    gender: 'Male',
    bloodGroup: 'Unknown',
    phone: '',
    address: '',
    abhaId: '',
    weight: '',
    height: '',
    // Emergency contact 1
    guardianName: '',
    guardianPhone: '',
    guardianRelation: '',
    // Emergency contact 2
    guardian2Name: '',
    guardian2Phone: '',
    guardian2Relation: '',
    // Clinical
    doctor: '',
    bedId: '',
    admitType: 'Medical Management',
    reason: '',
    diagnosis: '',
    icdCode: '',
    allergies: '',
    pastHistory: [],
    // Clinical flags
    isDnr: false,
    isIsolation: false,
    isFallRisk: false,
    isMlc: false,
    mlcNo: '',
    // Referral
    referredBy: '',
    referredFrom: '',
    // Financial
    insurance: 'Cash',
    policyNo: '',
    tpaName: '',
    tpaPreAuthNo: '',
    advanceAmount: '',
    advanceMode: 'Cash',
    advanceRef: '',
  });

  useEffect(() => {
    if (doctors.length && !form.doctor) {
      setForm((f) => ({ ...f, doctor: doctors[0].name }));
    }
  }, [doctors, form.doctor]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [step, setStep] = useState(1); // 1: Patient Info, 2: Clinical & Bed, 3: Finance & Confirm
  const [opHandoff, setOpHandoff] = useState(null);

  // Patient search picker state
  const [patientResults, setPatientResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [patientSearching, setPatientSearching] = useState(false);
  const [patientSearchError, setPatientSearchError] = useState('');
  const searchTimerRef = useRef(null);

  const queueId = params.get('queueId') || '';
  const urlDoctor = params.get('doctor') || '';
  const urlReason = params.get('reason') || '';
  const urlDiagnosis = params.get('diagnosis') || '';

  useEffect(() => {
    const patientId = params.get('patientId');
    const phoneFromUrl = params.get('phone') || '';
    const nameFromUrl = params.get('patient') || '';

    // Always apply OP handoff query params (even when UHID is missing)
    if (queueId || urlDoctor || urlReason || urlDiagnosis || phoneFromUrl || nameFromUrl) {
      setForm((f) => ({
        ...f,
        patientName: f.patientName || nameFromUrl,
        phone: f.phone || phoneFromUrl,
        doctor: urlDoctor || f.doctor,
        reason: urlReason || f.reason,
        diagnosis: urlDiagnosis || f.diagnosis,
        referredBy: urlDoctor || f.referredBy,
        referredFrom: queueId ? 'OPD Queue' : f.referredFrom,
      }));
      if (queueId || urlDoctor || urlReason) {
        setOpHandoff({ queueId, doctor: urlDoctor, reason: urlReason });
      }
    }

    if (!patientId) return;
    let cancelled = false;
    (async () => {
      const profile = await fetchPatientProfile(patientId);
      if (cancelled) return;
      const chronic = Array.isArray(profile?.chronicConditions)
        ? profile.chronicConditions
        : (profile?.chronicConditions ? String(profile.chronicConditions).split(',').map((s) => s.trim()).filter(Boolean) : []);
      setForm((f) => ({
        ...f,
        patientName: f.patientName || profile?.name || nameFromUrl || '',
        patientId: profile?.patientId || patientId,
        age: f.age || profile?.age || '',
        gender: profile?.gender ? genderFromCode(profile.gender) : f.gender,
        bloodGroup: profile?.bloodGroup || f.bloodGroup,
        phone: f.phone || profile?.phone || phoneFromUrl || '',
        address: f.address || [profile?.address, profile?.city, profile?.pincode].filter(Boolean).join(', '),
        abhaId: f.abhaId || profile?.abhaId || '',
        allergies: f.allergies || profile?.allergies || '',
        guardianName: f.guardianName || profile?.emergencyName || '',
        guardianPhone: f.guardianPhone || profile?.emergencyPhone || '',
        pastHistory: chronic.length ? chronic : f.pastHistory,
        doctor: urlDoctor || f.doctor,
        reason: urlReason || f.reason,
        diagnosis: urlDiagnosis || f.diagnosis,
        referredBy: urlDoctor || f.referredBy,
        referredFrom: queueId ? 'OPD Queue' : f.referredFrom,
      }));
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get('patientId'), queueId, urlDoctor, urlReason, urlDiagnosis]);

  const selectedBed = vacantBeds.find((b) => b.bedId === form.bedId || b.id === form.bedId);
  const ratePerDay = selectedBed ? (selectedBed.ratePerDay || selectedBed.rate || 1500) : 0;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    try {
      const res = await ipApi.admitPatient({
        patientName: form.patientName,
        patientId: form.patientId,
        age: form.age,
        gender: form.gender,
        bloodGroup: form.bloodGroup,
        phone: form.phone,
        address: form.address,
        guardianName: form.guardianName,
        guardianPhone: form.guardianPhone,
        guardianRelation: form.guardianRelation,
        guardian2Name: form.guardian2Name,
        guardian2Phone: form.guardian2Phone,
        guardian2Relation: form.guardian2Relation,
        doctor: form.doctor,
        bedId: selectedBed?.bedId || selectedBed?.id,
        bedLabel: selectedBed ? `${selectedBed.ward} · ${selectedBed.room} · ${selectedBed.bed}` : '',
        ward: selectedBed?.ward,
        ratePerDay,
        insurance: form.insurance,
        policyNo: form.policyNo,
        tpaName: form.tpaName,
        tpaPreAuthNo: form.tpaPreAuthNo,
        reason: form.reason,
        diagnosis: form.diagnosis,
        icdCode: form.icdCode,
        allergies: form.allergies,
        pastHistory: form.pastHistory,
        isDnr: form.isDnr,
        isIsolation: form.isIsolation,
        isFallRisk: form.isFallRisk,
        isMlc: form.isMlc,
        mlcNo: form.mlcNo,
        admitType: form.admitType,
        referredBy: form.referredBy,
        referredFrom: form.referredFrom || (queueId ? 'OPD Queue' : ''),
        sourceQueueId: queueId || undefined,
        opVisitReason: urlReason || undefined,
        advanceAmount: Number(form.advanceAmount) || 0,
        advanceMode: form.advanceMode,
        advanceRef: form.advanceRef,
        hospitalId: user.hospitalId,
      });
      // Backend marks queue Completed + syncs patientId; no client Completed patch (reception is blocked)
      const admissionId = res?.admission?.admissionId;
      if (admissionId) {
        navigate(`/ipd/chart/${admissionId}`);
      } else {
        navigate('/ipd/list');
      }
    } catch (err) {
      setSaveError(err.message);
      setStep(3);
    } finally {
      setSaving(false);
    }
  };

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleHistory = (item) => setForm((f) => ({
    ...f,
    pastHistory: f.pastHistory.includes(item)
      ? f.pastHistory.filter((h) => h !== item)
      : [...f.pastHistory, item],
  }));

  const handlePatientNameChange = (value) => {
    setF({ patientName: value });
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!value.trim()) {
      setPatientResults([]);
      setShowDropdown(false);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setPatientSearching(true);
      try {
        const res = await ipApi.getPatients(value.trim(), 15);
        const list = res?.patients || res?.items || [];
        setPatientResults(list);
        setShowDropdown(list.length > 0);
        setPatientSearchError('');
      } catch (e) {
        setPatientResults([]);
        setShowDropdown(false);
        setPatientSearchError(e.message || 'Patient search failed');
      } finally {
        setPatientSearching(false);
      }
    }, 300);
  };

  const pickPatient = (p) => {
    const chronic = Array.isArray(p?.chronicConditions)
      ? p.chronicConditions
      : (p?.chronicConditions ? String(p.chronicConditions).split(',').map((s) => s.trim()).filter(Boolean) : []);
    setForm((f) => ({
      ...f,
      patientName: p.name || f.patientName,
      patientId: p.patientId || f.patientId,
      age: p.age || f.age,
      gender: p.gender ? genderFromCode(p.gender) : f.gender,
      bloodGroup: p.bloodGroup || f.bloodGroup,
      phone: p.phone || f.phone,
      address: [p.address, p.city, p.pincode].filter(Boolean).join(', ') || f.address,
      abhaId: p.abhaId || f.abhaId,
      allergies: p.allergies || f.allergies,
      guardianName: p.emergencyName || f.guardianName,
      guardianPhone: p.emergencyPhone || f.guardianPhone,
      pastHistory: chronic.length ? chronic : f.pastHistory,
    }));
    setPatientResults([]);
    setShowDropdown(false);
  };

  const stepTitles = ['Patient Details', 'Clinical & Bed', 'History & Flags', 'Finance & Confirm'];

  return (
    <PageShell
      title="Admit Patient to IPD"
      subtitle="3-step admission · Bed allocation · Advance payment"
    >
      {(error || saveError) && <div className="error mb">{error || saveError}</div>}

      {opHandoff && (
        <div className="info-banner mb" style={{ background: '#f0fdfa', border: '1px solid #99f6e4', color: '#0f766e' }}>
          <strong>OP → IP handoff</strong>
          {opHandoff.doctor && <> · Referred by {opHandoff.doctor}</>}
          {opHandoff.reason && <> · Chief complaint: {opHandoff.reason}</>}
          {opHandoff.queueId && <> · OP queue ref: {opHandoff.queueId}</>}
        </div>
      )}

      {/* Step indicator */}
      <div className="admit-stepper card mb">
        {stepTitles.map((title, i) => (
          <div
            key={i}
            className={`admit-step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}
            onClick={() => step > i + 1 && setStep(i + 1)}
            style={{ cursor: step > i + 1 ? 'pointer' : 'default' }}
          >
            <div className="admit-step-num">{step > i + 1 ? '✓' : i + 1}</div>
            <div className="admit-step-label">{title}</div>
          </div>
        ))}
      </div>

      {loading ? <p className="hint">Loading beds…</p> : (
        <form onSubmit={submit}>
          {/* ── STEP 1: Patient Details ── */}
          {step === 1 && (
            <div className="card">
              <h3 style={{ marginTop: 0, marginBottom: 16 }}>🧑 Patient Information</h3>
              <div className="form-grid">
                {/* Patient name with live search picker */}
                <div className="field full" style={{ position: 'relative' }}>
                  <label>Patient Full Name *</label>
                  <input
                    className="input"
                    required
                    value={form.patientName}
                    onChange={(e) => handlePatientNameChange(e.target.value)}
                    onFocus={() => { if (patientResults.length > 0) setShowDropdown(true); }}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 180)}
                    placeholder="Type name, phone, or UHID to search — or enter new patient"
                    autoComplete="off"
                  />
                  {patientSearching && (
                    <span style={{ position: 'absolute', right: 10, bottom: 10, fontSize: 11, color: '#94a3b8', pointerEvents: 'none' }}>Searching…</span>
                  )}
                  {patientSearchError && <div className="error" style={{ marginTop: 6, fontSize: 12 }}>{patientSearchError}</div>}
                  {showDropdown && patientResults.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                      background: '#fff', border: '1.5px solid #6366f1',
                      borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      maxHeight: 240, overflowY: 'auto', marginTop: 2,
                    }}>
                      {patientResults.map((p) => (
                        <div
                          key={p.patientId || p.name}
                          onMouseDown={(e) => { e.preventDefault(); pickPatient(p); }}
                          style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', transition: 'background 0.12s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f5f3ff'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                          <div>
                            <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>{p.name}</div>
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
                              {[p.age ? `${p.age}y` : null, p.gender, p.phone].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                          <span style={{ background: '#ede9fe', color: '#6d28d9', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: 'monospace', marginLeft: 10, flexShrink: 0 }}>
                            {p.patientId}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="field">
                  <label>Patient ID (UHID — auto-filled on search)</label>
                  <input className="input" value={form.patientId} onChange={(e) => setF({ patientId: e.target.value })} placeholder="PT-0001 (filled when patient selected)" />
                </div>
                <div className="field">
                  <label>ABHA ID (Ayushman Bharat)</label>
                  <input className="input" value={form.abhaId} onChange={(e) => setF({ abhaId: e.target.value })} placeholder="14-digit ABHA number" />
                </div>
                <div className="field">
                  <label>Age</label>
                  <input className="input" type="number" min={0} max={120} value={form.age} onChange={(e) => setF({ age: e.target.value })} placeholder="e.g. 45" />
                </div>
                <div className="field">
                  <label>Gender</label>
                  <select className="input" value={form.gender} onChange={(e) => setF({ gender: e.target.value })}>
                    {['Male', 'Female', 'Other'].map((g) => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Blood Group</label>
                  <select className="input" value={form.bloodGroup} onChange={(e) => setF({ bloodGroup: e.target.value })}>
                    {BLOOD_GROUPS.map((g) => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Phone number</label>
                  <input className="input" type="tel" value={form.phone} onChange={(e) => setF({ phone: e.target.value })} placeholder="9876543210" />
                </div>
                <div className="field">
                  <label>Weight (kg)</label>
                  <input className="input" type="number" value={form.weight} onChange={(e) => setF({ weight: e.target.value })} placeholder="e.g. 65" />
                </div>
                <div className="field">
                  <label>Height (cm)</label>
                  <input className="input" type="number" value={form.height} onChange={(e) => setF({ height: e.target.value })} placeholder="e.g. 165" />
                </div>
                <div className="field full">
                  <label>Address</label>
                  <input className="input" value={form.address} onChange={(e) => setF({ address: e.target.value })} placeholder="City, State" />
                </div>

                <h4 style={{ gridColumn: '1/-1', margin: '8px 0 4px', color: '#475569', fontSize: 13, fontWeight: 700, textTransform: 'uppercase' }}>👨‍👩‍👧 Emergency Contact 1</h4>
                <div className="field">
                  <label>Guardian / Attender Name *</label>
                  <input className="input" required value={form.guardianName} onChange={(e) => setF({ guardianName: e.target.value })} placeholder="Name of guardian" />
                </div>
                <div className="field">
                  <label>Relation</label>
                  <select className="input" value={form.guardianRelation} onChange={(e) => setF({ guardianRelation: e.target.value })}>
                    {['', 'Spouse', 'Father', 'Mother', 'Son', 'Daughter', 'Sibling', 'Friend', 'Other'].map((r) => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Guardian Phone *</label>
                  <input className="input" required type="tel" value={form.guardianPhone} onChange={(e) => setF({ guardianPhone: e.target.value })} placeholder="Guardian mobile number" />
                </div>

                <h4 style={{ gridColumn: '1/-1', margin: '8px 0 4px', color: '#475569', fontSize: 13, fontWeight: 700, textTransform: 'uppercase' }}>📞 Emergency Contact 2 (optional)</h4>
                <div className="field">
                  <label>Name</label>
                  <input className="input" value={form.guardian2Name} onChange={(e) => setF({ guardian2Name: e.target.value })} placeholder="Name (optional)" />
                </div>
                <div className="field">
                  <label>Relation</label>
                  <select className="input" value={form.guardian2Relation} onChange={(e) => setF({ guardian2Relation: e.target.value })}>
                    {['', 'Spouse', 'Father', 'Mother', 'Son', 'Daughter', 'Sibling', 'Friend', 'Other'].map((r) => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Phone</label>
                  <input className="input" type="tel" value={form.guardian2Phone} onChange={(e) => setF({ guardian2Phone: e.target.value })} placeholder="Mobile number" />
                </div>
              </div>
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/ipd/list')}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!form.patientName || !form.guardianName || !form.guardianPhone}
                  onClick={() => setStep(2)}
                >
                  Next: Clinical & Bed →
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 2: Clinical & Bed ── */}
          {step === 2 && (
            <div className="card">
              <h3 style={{ marginTop: 0, marginBottom: 16 }}>🏥 Clinical Details & Bed Allocation</h3>
              <div className="form-grid">
                <div className="field">
                  <label>Admitting Doctor *</label>
                  <select className="input" value={form.doctor} onChange={(e) => setF({ doctor: e.target.value })}>
                    {doctors.length ? doctors.map((d) => (
                      <option key={d.staffId} value={d.name}>{formatDoctorOption(d)}</option>
                    )) : <option value={form.doctor}>{form.doctor || 'Doctor'}</option>}
                  </select>
                </div>
                <div className="field">
                  <label>Admission Type</label>
                  <select className="input" value={form.admitType} onChange={(e) => setF({ admitType: e.target.value })}>
                    {ADMIT_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Referred By (Doctor)</label>
                  <input className="input" value={form.referredBy} onChange={(e) => setF({ referredBy: e.target.value })} placeholder="Dr. Name (optional)" />
                </div>
                <div className="field">
                  <label>Referred From (Hospital)</label>
                  <input className="input" value={form.referredFrom} onChange={(e) => setF({ referredFrom: e.target.value })} placeholder="Hospital name (optional)" />
                </div>
                <div className="field full">
                  <label>Chief Complaint / Reason for Admission *</label>
                  <textarea className="input" rows={2} required value={form.reason} onChange={(e) => setF({ reason: e.target.value })} placeholder="e.g. Acute appendicitis, for emergency appendectomy" />
                </div>
                <div className="field">
                  <label>Provisional Diagnosis</label>
                  <input className="input" value={form.diagnosis} onChange={(e) => setF({ diagnosis: e.target.value })} placeholder="e.g. Dengue fever with thrombocytopenia" />
                </div>
                <div className="field">
                  <label>ICD-10 Code (provisional)</label>
                  <input className="input" value={form.icdCode} onChange={(e) => setF({ icdCode: e.target.value })} placeholder="e.g. A90 (Dengue), K35.8 (Appendicitis)" />
                </div>
                <div className="field full">
                  <label>⚠ Known Allergies</label>
                  <input className="input" value={form.allergies} onChange={(e) => setF({ allergies: e.target.value })} placeholder="e.g. Penicillin, Sulfa drugs, Aspirin — None if none" />
                </div>

                {/* MLC Flag */}
                <div className="field full" style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:10, padding:'10px 14px' }}>
                  <label style={{ fontWeight:700, color:'#dc2626', display:'flex', alignItems:'center', gap:8 }}>
                    <input type="checkbox" checked={form.isMlc} onChange={(e) => setF({ isMlc: e.target.checked })} />
                    🚔 Medico-Legal Case (MLC)
                  </label>
                  {form.isMlc && (
                    <input className="input" style={{ marginTop:8 }} value={form.mlcNo} onChange={(e) => setF({ mlcNo: e.target.value })} placeholder="MLC number / police reference" />
                  )}
                </div>

                {/* Clinical Safety Flags */}
                <div className="field full">
                  <label style={{ fontWeight: 700, color: '#dc2626' }}>🚨 Critical Clinical Flags (will appear as alerts on chart)</label>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      <input type="checkbox" checked={form.isDnr} onChange={(e) => setF({ isDnr: e.target.checked })} />
                      <span style={{ background: '#1e1b4b', color: '#fff', padding: '2px 10px', borderRadius: 6, fontSize: 12 }}>DNR</span>
                      Do Not Resuscitate
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      <input type="checkbox" checked={form.isIsolation} onChange={(e) => setF({ isIsolation: e.target.checked })} />
                      <span style={{ background: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa', padding: '2px 10px', borderRadius: 6, fontSize: 12 }}>🚫 ISOLATION</span>
                      Isolation Precautions Required
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      <input type="checkbox" checked={form.isFallRisk} onChange={(e) => setF({ isFallRisk: e.target.checked })} />
                      <span style={{ background: '#fefce8', color: '#ca8a04', border: '1px solid #fde68a', padding: '2px 10px', borderRadius: 6, fontSize: 12 }}>⚠ FALL RISK</span>
                      Patient at Risk of Falls
                    </label>
                  </div>
                </div>

                <h4 style={{ gridColumn: '1/-1', margin: '8px 0 4px', color: '#475569', fontSize: 13, fontWeight: 700, textTransform: 'uppercase' }}>🛏 Bed Selection</h4>

                <div className="field full">
                  <label>Select Bed *</label>
                  <select className="input" required value={form.bedId} onChange={(e) => setF({ bedId: e.target.value })}>
                    <option value="">— Choose a vacant bed —</option>
                    {vacantBeds.map((b) => (
                      <option key={b.bedId || b.id} value={b.bedId || b.id}>
                        {b.ward} · {b.room} · {b.bed} — ₹{(b.ratePerDay || b.rate || 0).toLocaleString('en-IN')}/day
                      </option>
                    ))}
                  </select>
                  {vacantBeds.length === 0 && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>⚠ No vacant beds. Please add beds in Bed Master or discharge a patient.</p>}
                </div>

                {selectedBed && (
                  <div className="info-banner" style={{ gridColumn: '1/-1' }}>
                    ✅ Selected: <strong>{selectedBed.ward} · {selectedBed.room} · Bed {selectedBed.bed}</strong> at <strong>₹{ratePerDay.toLocaleString('en-IN')}/day</strong>
                  </div>
                )}
              </div>
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>← Back</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!form.doctor || !form.bedId || !form.reason}
                  onClick={() => setStep(3)}
                >
                  Next: Medical History →
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: Medical History & Past History ── */}
          {step === 3 && (
            <div className="card">
              <h3 style={{ marginTop: 0, marginBottom: 16 }}>📋 Medical History & Past History</h3>
              <div className="form-grid">
                <div className="field full">
                  <label style={{ fontWeight:700, marginBottom:8, display:'block' }}>Past Medical History (tick all that apply)</label>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {PAST_HISTORY.map(item => (
                      <label key={item} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13, padding:'5px 12px', background: form.pastHistory.includes(item)?'#eff6ff':'#f8fafc', border:`1px solid ${form.pastHistory.includes(item)?'#bfdbfe':'#e2e8f0'}`, borderRadius:8, fontWeight: form.pastHistory.includes(item)?700:400, color: form.pastHistory.includes(item)?'#2563eb':'#475569' }}>
                        <input type="checkbox" checked={form.pastHistory.includes(item)} onChange={() => toggleHistory(item)} style={{ accentColor:'#2563eb' }} />
                        {item}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setStep(2)}>← Back</button>
                <button type="button" className="btn btn-primary" onClick={() => setStep(4)}>Next: Finance & Confirm →</button>
              </div>
            </div>
          )}

          {/* ── STEP 4: Finance & Confirm ── */}
          {step === 4 && (
            <div>
              <div className="card mb">
                <h3 style={{ marginTop: 0, marginBottom: 16 }}>💰 Insurance & Advance Payment</h3>
                <div className="form-grid">
                  <div className="field">
                    <label>Payment Type</label>
                    <select className="input" value={form.insurance} onChange={(e) => setF({ insurance: e.target.value })}>
                      {INSURANCE_OPTIONS.map((i) => <option key={i}>{i}</option>)}
                    </select>
                  </div>
                  {form.insurance !== 'Cash' && (
                    <>
                      <div className="field">
                        <label>Policy / Member ID</label>
                        <input className="input" value={form.policyNo} onChange={(e) => setF({ policyNo: e.target.value })} placeholder="e.g. SH-2024-001234" />
                      </div>
                      <div className="field">
                        <label>TPA Name</label>
                        <input className="input" value={form.tpaName} onChange={(e) => setF({ tpaName: e.target.value })} placeholder="Mediassist, Paramount, etc." />
                      </div>
                      <div className="field">
                        <label>Pre-Auth Number (if available)</label>
                        <input className="input" value={form.tpaPreAuthNo} onChange={(e) => setF({ tpaPreAuthNo: e.target.value })} placeholder="Pre-authorization number" />
                      </div>
                    </>
                  )}
                  <div className="field">
                    <label>Advance Amount ₹</label>
                    <input className="input" type="number" min={0} value={form.advanceAmount} onChange={(e) => setF({ advanceAmount: e.target.value })} placeholder="e.g. 5000" />
                  </div>
                  <div className="field">
                    <label>Advance Payment Mode</label>
                    <select className="input" value={form.advanceMode} onChange={(e) => setF({ advanceMode: e.target.value })}>
                      {['Cash', 'UPI', 'Card', 'Bank Transfer', 'Cheque'].map((m) => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                  {form.advanceMode !== 'Cash' && (
                    <div className="field">
                      <label>Transaction Ref / UPI ID / Cheque No.</label>
                      <input className="input" value={form.advanceRef} onChange={(e) => setF({ advanceRef: e.target.value })} placeholder="Reference number" />
                    </div>
                  )}
                </div>
              </div>

              {/* Admission Summary */}
              <div className="card mb">
                <h3 style={{ marginTop: 0 }}>📋 Admission Summary</h3>
                <div className="admit-summary-grid">
                  <div className="admit-summary-section">
                    <div className="admit-summary-title">Patient</div>
                    <div className="admit-summary-row"><span>Name</span><strong>{form.patientName}</strong></div>
                    <div className="admit-summary-row"><span>Age / Gender</span><strong>{form.age || '—'} / {form.gender}</strong></div>
                    <div className="admit-summary-row"><span>Blood Group</span><strong>{form.bloodGroup}</strong></div>
                    {form.weight && <div className="admit-summary-row"><span>Weight / Height</span><strong>{form.weight}kg / {form.height || '—'}cm</strong></div>}
                    <div className="admit-summary-row"><span>Phone</span><strong>{form.phone || '—'}</strong></div>
                    {form.abhaId && <div className="admit-summary-row"><span>ABHA ID</span><strong>{form.abhaId}</strong></div>}
                    <div className="admit-summary-row"><span>Guardian</span><strong>{form.guardianName} ({form.guardianRelation || 'Guardian'})</strong></div>
                    <div className="admit-summary-row"><span>Guardian Phone</span><strong>{form.guardianPhone}</strong></div>
                    {form.guardian2Name && <div className="admit-summary-row"><span>Guardian 2</span><strong>{form.guardian2Name} · {form.guardian2Phone}</strong></div>}
                  </div>
                  <div className="admit-summary-section">
                    <div className="admit-summary-title">Clinical</div>
                    <div className="admit-summary-row"><span>Doctor</span><strong>{form.doctor}</strong></div>
                    <div className="admit-summary-row"><span>Admit Type</span><strong>{form.admitType}</strong></div>
                    <div className="admit-summary-row"><span>Reason</span><strong>{form.reason}</strong></div>
                    {form.diagnosis && <div className="admit-summary-row"><span>Diagnosis</span><strong>{form.diagnosis}</strong></div>}
                    {form.icdCode && <div className="admit-summary-row"><span>ICD-10</span><strong>{form.icdCode}</strong></div>}
                    {form.allergies && <div className="admit-summary-row"><span>Allergies</span><strong style={{color:'#ef4444'}}>{form.allergies}</strong></div>}
                    {form.isMlc && <div className="admit-summary-row"><span>MLC</span><strong style={{color:'#dc2626'}}>🚔 Yes — {form.mlcNo}</strong></div>}
                    {form.pastHistory.length > 0 && <div className="admit-summary-row"><span>Past History</span><strong>{form.pastHistory.join(', ')}</strong></div>}
                    {form.referredBy && <div className="admit-summary-row"><span>Referred By</span><strong>{form.referredBy}</strong></div>}
                  </div>
                  <div className="admit-summary-section">
                    <div className="admit-summary-title">Bed & Finance</div>
                    {selectedBed && <>
                      <div className="admit-summary-row"><span>Ward</span><strong>{selectedBed.ward}</strong></div>
                      <div className="admit-summary-row"><span>Room / Bed</span><strong>{selectedBed.room} · {selectedBed.bed}</strong></div>
                      <div className="admit-summary-row"><span>Rate/day</span><strong style={{ color: '#0d6e6a' }}>₹{ratePerDay.toLocaleString('en-IN')}</strong></div>
                    </>}
                    <div className="admit-summary-row"><span>Insurance</span><strong>{form.insurance}</strong></div>
                    {form.policyNo && <div className="admit-summary-row"><span>Policy No.</span><strong>{form.policyNo}</strong></div>}
                    {form.tpaPreAuthNo && <div className="admit-summary-row"><span>Pre-Auth No.</span><strong>{form.tpaPreAuthNo}</strong></div>}
                    <div className="admit-summary-row">
                      <span>Advance Paid</span>
                      <strong style={{ color: form.advanceAmount ? '#10b981' : '#94a3b8' }}>
                        {form.advanceAmount ? `₹${Number(form.advanceAmount).toLocaleString('en-IN')} (${form.advanceMode})` : 'None'}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setStep(3)}>← Back</button>
                <button type="submit" className="btn btn-primary" disabled={saving} style={{ minWidth: 200 }}>
                  {saving ? '⏳ Admitting…' : '✅ Confirm Admission & Generate IP ID'}
                </button>
              </div>
            </div>
          )}
        </form>
      )}
    </PageShell>
  );
}

export function BedMaster() {
  const { user } = useAuth();
  const { data, loading, error, reload } = useLiveData(() => ipApi.getBeds(), []);
  const beds = data?.beds || [];
  const canEdit = user?.role === 'manager' || user?.role === 'ipd';
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [editId, setEditId] = useState('');
  const [form, setForm] = useState({ ward: 'General', room: '', bed: '', type: 'General', ratePerDay: 1500, status: 'vacant' });

  const stats = {
    total: beds.length,
    vacant: beds.filter((b) => b.status === 'vacant').length,
    occupied: beds.filter((b) => b.status !== 'vacant').length,
  };

  const resetForm = () => {
    setForm({ ward: 'General', room: '', bed: '', type: 'General', ratePerDay: 1500, status: 'vacant' });
    setEditId('');
    setShowForm(false);
  };

  const startEdit = (b) => {
    setEditId(b.bedId);
    setForm({
      ward: b.ward || 'General',
      room: b.room || '',
      bed: b.bed || '',
      type: b.type || b.ward || 'General',
      ratePerDay: Number(b.ratePerDay || b.rate || 1500),
      status: b.status === 'occupied' ? 'occupied' : 'vacant',
    });
    setShowForm(true);
  };

  const saveBed = async (e) => {
    e.preventDefault();
    if (!form.ward.trim() || !form.bed.trim()) {
      setMsg('Ward and bed label are required.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      if (editId) await ipApi.patchBed(editId, form);
      else await ipApi.createBed(form);
      setMsg(editId ? 'Bed updated.' : 'Bed added.');
      resetForm();
      await reload();
    } catch (err) {
      setMsg(err.message || 'Could not save bed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell
      title="Bed Master"
      subtitle="Ward · room · bed · daily rate (live)"
      actions={canEdit ? (
        <button type="button" className="btn btn-primary" onClick={() => { setEditId(''); setShowForm(true); }}>+ Add Bed</button>
      ) : null}
    >
      {error && <div className="error mb">{error}</div>}
      {msg && <div className={`mb ${/fail|error|could/i.test(msg) ? 'error' : 'info-banner'}`}>{msg}</div>}

      <div className="stat-grid" style={{ marginBottom: 14 }}>
        <div className="card" style={{ textAlign: 'center', padding: 16 }}>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 28, fontWeight: 900, color: '#0d6e6a' }}>{stats.total}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>Total Beds</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 16 }}>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 28, fontWeight: 900, color: '#10b981' }}>{stats.vacant}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>Vacant</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 16 }}>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 28, fontWeight: 900, color: '#f97316' }}>{stats.occupied}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>Occupied</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 16 }}>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 28, fontWeight: 900, color: '#8b5cf6' }}>
            {stats.total > 0 ? Math.round((stats.occupied / stats.total) * 100) : 0}%
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>Occupancy Rate</div>
        </div>
      </div>

      {showForm && canEdit && (
        <form className="card mb" onSubmit={saveBed} style={{ borderLeft: '4px solid #0d6e6a' }}>
          <h3 style={{ marginTop: 0 }}>{editId ? 'Edit Bed' : 'Add Bed'}</h3>
          <div className="form-grid mb">
            <div className="field">
              <label>Ward *</label>
              <select className="input" value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value, type: e.target.value })}>
                {['General', 'General Male', 'General Female', 'Private', 'Semi-Private', 'ICU', 'NICU', 'PICU', 'Maternity', 'Isolation'].map((w) => (
                  <option key={w}>{w}</option>
                ))}
              </select>
            </div>
            <div className="field"><label>Room</label><input className="input" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} placeholder="Room 1 / 204" /></div>
            <div className="field"><label>Bed label *</label><input className="input" required value={form.bed} onChange={(e) => setForm({ ...form, bed: e.target.value })} placeholder="Bed 1 / A" /></div>
            <div className="field"><label>Rate ₹/day *</label><input className="input" type="number" min={0} required value={form.ratePerDay} onChange={(e) => setForm({ ...form, ratePerDay: e.target.value })} /></div>
            <div className="field">
              <label>Status</label>
              <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} disabled={!editId}>
                <option value="vacant">Vacant</option>
                <option value="occupied">Occupied</option>
              </select>
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : (editId ? 'Update Bed' : 'Add Bed')}</button>
          </div>
        </form>
      )}

      {loading ? <p className="hint">Loading…</p> : (
        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Ward', 'Room', 'Bed', 'Type', 'Rate/day', 'Status', ...(canEdit ? ['Action'] : [])].map((h) => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: 'left', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {beds.map((b) => (
                  <tr key={b.bedId || b.id}>
                    <td style={{ padding: '8px 12px', border: '1px solid #f1f5f9', fontWeight: 600 }}>{b.ward}</td>
                    <td style={{ padding: '8px 12px', border: '1px solid #f1f5f9' }}>{b.room}</td>
                    <td style={{ padding: '8px 12px', border: '1px solid #f1f5f9', fontFamily: 'monospace' }}>{b.bed}</td>
                    <td style={{ padding: '8px 12px', border: '1px solid #f1f5f9', color: '#64748b' }}>{b.type || b.ward}</td>
                    <td style={{ padding: '8px 12px', border: '1px solid #f1f5f9', fontWeight: 700, color: '#0d6e6a' }}>₹{(b.ratePerDay || b.rate || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '8px 12px', border: '1px solid #f1f5f9' }}>
                      <span style={{
                        fontSize: 11.5, fontWeight: 700, padding: '2px 10px', borderRadius: 999,
                        background: b.status === 'vacant' ? '#f0fdf4' : '#fef2f2',
                        color: b.status === 'vacant' ? '#16a34a' : '#dc2626',
                        border: `1px solid ${b.status === 'vacant' ? '#86efac' : '#fca5a5'}`,
                      }}>
                        {b.status === 'vacant' ? '✓ Vacant' : '⚠ Occupied'}
                      </span>
                    </td>
                    {canEdit && (
                      <td style={{ padding: '8px 12px', border: '1px solid #f1f5f9' }}>
                        <button type="button" className="btn btn-sm btn-secondary" onClick={() => startEdit(b)}>Edit</button>
                      </td>
                    )}
                  </tr>
                ))}
                {beds.length === 0 && (
                  <tr>
                    <td colSpan={canEdit ? 7 : 6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                      No beds configured. {canEdit ? 'Click “+ Add Bed” above to create your first bed.' : 'Ask Manager / IPD coordinator to add beds.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </PageShell>
  );
}
